import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle2, AlertTriangle, XCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useRealtimeNotificationsStore,
  type NotificationKind,
} from '@/stores/useRealtimeNotificationsStore';
import { usePlatformNotifications } from '@/hooks/usePlatformNotifications';
import type { AppNotification } from '@/integrations/api/notifications_api';
import { cn } from '@/lib/utils';

const ICONS: Record<NotificationKind, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

const ACCENTS: Record<NotificationKind, string> = {
  success: 'text-emerald-500',
  warning: 'text-amber-500',
  error: 'text-destructive',
  info: 'text-primary',
};

function timeAgo(ts: number | string): string {
  const ms = typeof ts === 'string' ? new Date(ts).getTime() : ts;
  const s = Math.max(1, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  return `hace ${h}h`;
}

function apiKind(n: AppNotification): NotificationKind {
  if (n.category === 'security') return 'warning';
  if (n.type.includes('revoked') || n.type.includes('deactivated')) return 'error';
  if (n.type.includes('accepted') || n.type.includes('activated')) return 'success';
  return 'info';
}

export function NotificationBell() {
  const navigate = useNavigate();
  const mockItems = useRealtimeNotificationsStore((s) => s.items);
  const markAllReadMock = useRealtimeNotificationsStore((s) => s.markAllRead);
  const platform = usePlatformNotifications();

  const useApi = platform.enabled;
  const apiItems = platform.items.slice(0, 8);
  const mockRecent = mockItems.slice(0, 5);
  const unread = useApi ? platform.unread : mockItems.filter((i) => !i.read).length;

  const handleClose = (open: boolean) => {
    if (!open && unread > 0) {
      if (useApi) void platform.markAllRead();
      else markAllReadMock();
    }
  };

  return (
    <DropdownMenu onOpenChange={handleClose}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8" aria-label="Notificaciones">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <Badge className="absolute -right-1 -top-1 h-4 min-w-4 px-1 text-[10px] bg-destructive text-destructive-foreground">
              {unread > 9 ? '9+' : unread}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Notificaciones
          </span>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => {
                if (useApi) void platform.markAllRead();
                else markAllReadMock();
              }}
              className="text-[10px] text-primary hover:underline"
            >
              Marcar como leído
            </button>
          )}
        </div>

        {useApi ? (
          apiItems.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              Sin notificaciones recientes
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-border/60">
              {apiItems.map((n) => {
                const kind = apiKind(n);
                const Icon = ICONS[kind];
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => {
                      if (!n.read_at) void platform.markRead(n.id);
                      if (n.link) navigate(n.link);
                    }}
                    className={cn(
                      'w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-start gap-2',
                      !n.read_at && 'bg-accent/30',
                    )}
                  >
                    <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', ACCENTS[kind])} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium truncate">{n.title}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {timeAgo(n.created_at)}
                        </span>
                      </div>
                      {n.body && (
                        <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )
        ) : mockRecent.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            Sin notificaciones recientes
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-border/60">
            {mockRecent.map((n) => {
              const Icon = ICONS[n.kind];
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => n.link && navigate(n.link)}
                  className={cn(
                    'w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-start gap-2',
                    !n.read && 'bg-accent/30',
                  )}
                >
                  <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', ACCENTS[n.kind])} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium truncate">{n.title}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(n.ts)}</span>
                    </div>
                    {n.description && (
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.description}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
